import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CreditCard } from 'lucide-react';
import { api, ApiError } from '../../api.ts';
import { TopBar } from '../../components/TopBar.tsx';
import { BottomNav } from '../../components/BottomNav.tsx';
import { CardSetup, cardLabel, type SavedCard } from '../../components/CardSetup.tsx';
import { PAYMENT_ERRORS } from '../../stripeClient.ts';
import type { Organization } from '../../types.ts';

/** Moyen de paiement de l'établissement : une carte, remplaçable. Elle sert à bloquer le montant des missions réservées. */
export function OrgCard() {
  const [params] = useSearchParams();
  const returnTo = params.get('retour');
  const [org, setOrg] = useState<Organization | null>(null);
  const [card, setCard] = useState<SavedCard | null | 'loading'>('loading');
  const [editing, setEditing] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  useEffect(() => {
    api.get<{ organizations: Organization[] }>('/organizations/mine').then(async (res) => {
      const first = res.organizations[0];
      setOrg(first);
      if (first) setCard((await api.get<{ card: SavedCard | null }>(`/payments/org/card?organizationId=${first.id}`)).card);
    });
  }, []);

  async function remove() {
    if (!org || !window.confirm('Retirer cette carte ?')) return;
    setMessage(null);
    try {
      await api.delete(`/payments/org/card?organizationId=${org.id}`);
      setCard(null);
    } catch (err) {
      setMessage({ kind: 'error', text: err instanceof ApiError ? PAYMENT_ERRORS[err.code] ?? 'Suppression impossible.' : 'Suppression impossible.' });
    }
  }

  return (
    <div className="app-shell">
      <TopBar title="Moyen de paiement" back />
      <div className="screen-content">
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
          Votre carte sert à garantir le paiement des missions réservées : le montant est bloqué 48 heures avant le début, puis débité une fois les heures validées. Mon Extra ne voit jamais le numéro de votre carte.
        </div>
        {message && <div className={message.kind === 'ok' ? 'badge badge-available' : 'error-banner'} style={{ padding: '10px 14px' }}>{message.text}</div>}

        {org && card !== 'loading' && (
          <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {card && !editing && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <div style={{ fontWeight: 700 }}><CreditCard size={15} style={{ verticalAlign: -2 }} /> {cardLabel(card)}</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => setEditing(true)}>Remplacer</button>
                  <button className="btn btn-danger-outline btn-sm" onClick={remove}>Retirer</button>
                </div>
              </div>
            )}
            {(!card || editing) && (
              <>
                {!card && <div style={{ fontWeight: 800 }}>Ajouter une carte</div>}
                <CardSetup
                  organizationId={org.id}
                  onSaved={(c) => { setCard(c); setEditing(false); setMessage({ kind: 'ok', text: 'Carte enregistrée.' }); }}
                  onCancel={card ? () => setEditing(false) : undefined}
                />
              </>
            )}
          </div>
        )}

        {returnTo && card && card !== 'loading' && (
          <Link to={returnTo} className="btn btn-primary">Retour à la mission</Link>
        )}
      </div>
      <BottomNav />
    </div>
  );
}
